import React, { useState, useEffect, useCallback } from 'react';
import { collection, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import StudentIDCard from './StudentIDCard';
import './AdminStudentsView.css';
import './AdminDetailPages.css';
import StatCards from './StatCards';
import { adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

const SCHOOL_CODES = {
  'Beach Allen Primary School': 'BAP',
  'Bronte-Welsh Primary School': 'BWP',
  'Cayon Primary School': 'CPS',
  'Christ Church Nichola Town Primary School': 'CCN',
  'Deane-Glasford Primary School': 'DGP',
  'Dieppe Bay Primary School': 'DBP',
  'Dr. William Connor Primary School': 'DWC',
  'Edgar T. Morris Primary School': 'ETM',
  'Estridge Primary School': 'EPS',
  'Halliday-Smith Primary School': 'HSP',
  'Joshua Obadiah Williams Primary School': 'JOW',
  'Newton Ground Primary School': 'NGP',
  'Saddlers Primary School': 'SPS',
  'Sandy Point Primary School': 'SPP',
  "St. Paul's Primary School": 'SPP',
  'Tucker-Clarke Primary School': 'TCP',
  'Tyrell Williams Primary School': 'TWP',
  'Violet Petty Primary School': 'VPP',
  'Basseterre High School': 'BHS',
  'Cayon High School': 'CHS',
  'Charles E. Mills Secondary School': 'CEM',
  'Dr. Denzil L. Douglas Secondary School': 'DDS',
  'Verchilds High School': 'VHS',
  'Washington Archibald High School': 'WAH',
  'Clarence Fitzroy Bryant College (CFBC)': 'CFB',
  'UWI Open Campus - St. Kitts': 'UWI',
};

function generateStudentCode(schoolId) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  const year = new Date().getFullYear();
  // Try to resolve school code from schoolId (Firestore UUID) or name
  // schoolId may be a Firestore doc ID or school name depending on how it was stored
  let schoolCode = 'UNK';
  if (schoolId && SCHOOL_CODES[schoolId]) {
    schoolCode = SCHOOL_CODES[schoolId];
  }
  return `${schoolCode}-${code}-${year}`;
}

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const asUuid = (v) => (typeof v === 'string' && UUID_RE.test(v) ? v : null);

// iOS decodes dates with ISO8601DateFormatter (.withInternetDateTime) which does
// NOT accept fractional seconds — strip ".SSS" so the string parses on iOS.
const toIsoString = (d) => d.toISOString().replace(/\.\d{3}Z$/, 'Z');

function AdminStudentsView({ adminData }) {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [parents, setParents] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [detailStudent, setDetailStudent] = useState(null);
  const [detailClassName, setDetailClassName] = useState('');
  const [originalClassId, setOriginalClassId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [showIDCard, setShowIDCard] = useState(null);

  const [newStudent, setNewStudent] = useState({
    firstName: '',
    lastName: '',
    classId: '',
    birthDate: ''
  });

  // School-scoped cache key builder — shared with AdminAnalyticsView so tabs
  // reuse each other's fetched collections.
  const ck = (p) => `${adminSchoolIds(adminData).join('|') || 'all'}:${p}`;

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminStudents: Loading classes, schoolId:', adminData?.schoolId);
    try {
      let classesData = await dataCacheService.getDocs(ck('classes'), collection(db, 'classes'));
      if (adminData) {
        const sid = (adminData.schoolId || '').toLowerCase();
        classesData = sid ? classesData.filter(c => (c.schoolId || '').toLowerCase() === sid) : [];
      }
      classesData.sort(schoolClassSortOrder);
      console.log('🔥 AdminStudents: Classes loaded', classesData.length, classesData);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminStudents: Error loading classes:', error);
    }
  }, [adminData]);

  const loadStudents = useCallback(async () => {
    console.log('🔥 AdminStudents: Loading students');
    try {
      // Parallel + cached; filter isActive === true to match the old
      // where('isActive', '==', true) query (docs missing the flag are excluded).
      const perClass = await Promise.all(classes.map(cls =>
        dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students'))
      ));
      const studentsData = perClass.flat().filter(s => s.isActive === true);
      console.log('🔥 AdminStudents: Students loaded', studentsData.length, studentsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('🔥 AdminStudents: Error loading students:', error);
    } finally {
      setLoading(false);
    }
  }, [classes]);

  const loadParents = useCallback(async () => {
    console.log('🔥 AdminStudents: Loading parents, schoolId:', adminData?.schoolId);
    try {
      const sid = (adminData?.schoolId || '').toLowerCase();
      const parentUsers = (await dataCacheService.getDocs(ck('users'), collection(db, 'users')))
        .filter(u => u.role === 'Parent')
        .filter(u => !sid || (u.schoolId || '').toLowerCase() === sid);
      const parentsData = [];
      await Promise.all(parentUsers.map(async (parentUser) => {
        try {
          const parentDoc = await dataCacheService.getDoc(ck(`parents/${parentUser.id}`), doc(db, 'parents', parentUser.id));
          if (parentDoc) {
            parentsData.push({ ...parentDoc, username: parentUser.username });
          } else {
            parentsData.push({
              id: parentUser.id,
              fullName: `${parentUser.firstName || ''} ${parentUser.lastName || ''}`.trim() || parentUser.username || '',
              email: parentUser.email || '',
              phoneNumber: parentUser.phoneNumber || '',
              linkedChildrenIds: parentUser.linkedChildrenIds || [],
              isActive: parentUser.isActive !== false
            });
          }
        } catch (e) {
          console.error('🔥 AdminStudents: Error loading parent profile for', parentUser.id, e);
        }
      }));
      console.log('🔥 AdminStudents: Parents loaded', parentsData.length);
      setParents(parentsData);
    } catch (error) {
      console.error('🔥 AdminStudents: Error loading parents:', error);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    loadClasses();
    loadParents();
  }, [loadClasses, loadParents]);

  useEffect(() => {
    if (classes.length > 0) {
      loadStudents();
    }
  }, [classes, loadStudents]);

  const getFilteredStudents = () => {
    if (!searchText) {
      return students;
    }
    return students.filter(student =>
      student.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
      student.studentId.toLowerCase().includes(searchText.toLowerCase()) ||
      (student.email && student.email.toLowerCase().includes(searchText.toLowerCase()))
    );
  };

  const getStudentsByClass = () => {
    const filtered = getFilteredStudents();
    const grouped = {};
    classes.forEach(cls => {
      grouped[cls.id] = {
        class: cls,
        students: filtered.filter(s => s.classId === cls.id)
      };
    });
    return Object.values(grouped).filter(g => g.students.length > 0);
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    try {
      const birthDate = new Date(newStudent.birthDate);
      const age = new Date().getFullYear() - birthDate.getFullYear();
      const studentId = generateStudentCode(adminData?.schoolId);

      // iOS Student requires `id` decodable as a UUID — use a UUID for both the
      // document key and the `id` field (iOS writes doc id = id.uuidString).
      const studentUuid = crypto.randomUUID();
      await setDoc(doc(db, 'classes', newStudent.classId, 'students', studentUuid), {
        ...newStudent,
        id: studentUuid,
        uuid: studentUuid,
        studentId,
        fullName: `${newStudent.firstName} ${newStudent.lastName}`,
        birthDate: toIsoString(birthDate),
        age: age,
        classId: newStudent.classId,
        currentEnrollmentStatus: 'Active',
        isActive: true,
        createdAt: toIsoString(new Date()),
        schoolId: adminData?.schoolId || null
      });

      // Mirror the sign-up/linking record iOS writes (StudentService.saveStudentSignupRecord):
      // top-level students/{schoolIssuedStudentId} with the exact iOS field set.
      await setDoc(doc(db, 'students', studentId), {
        studentId: studentId,
        schoolId: adminData?.schoolId || null,
        classId: newStudent.classId || null,
        userId: null,
        email: null,
        firstName: newStudent.firstName,
        lastName: newStudent.lastName,
        updatedAt: toIsoString(new Date())
      }, { merge: true });

      dataCacheService.invalidatePath(`classes/${newStudent.classId}/students`);
      setShowAddForm(false);
      setNewStudent({ firstName: '', lastName: '', classId: '', birthDate: '' });
      loadStudents();
    } catch (error) {
      console.error('Error adding student:', error);
      alert('Failed to add student');
    }
  };

  const getStudentParents = (student) => {
    const studentKeys = [student.studentId, student.id, student.uuid]
      .filter(Boolean)
      .map(v => String(v).toLowerCase());
    if (studentKeys.length === 0) return [];
    return parents.filter(parent =>
      (parent.linkedChildrenIds || [])
        .map(id => String(id).toLowerCase())
        .some(id => studentKeys.includes(id))
    );
  };

  const handleDeleteStudent = async (student) => {
    const confirmed = window.confirm(
      `Are you sure you want to PERMANENTLY DELETE ${student.fullName}?\n\nThis action cannot be undone. All grades, attendance records, and assignments for this student will remain in the system but the student record will be removed.`
    );
    if (!confirmed) return false;
    try {
      await deleteDoc(doc(db, 'classes', student.classId, 'students', student.id));
      dataCacheService.invalidatePath(`classes/${student.classId}/students`);
      loadStudents();
      return true;
    } catch (error) {
      console.error('Error deleting student:', error);
      alert('Failed to delete student');
      return false;
    }
  };

  const handleEditClick = (student) => {
    const birthDate = student.birthDate ? new Date(student.birthDate).toISOString().split('T')[0] : '';
    setEditStudent({
      ...student,
      birthDate
    });
    setOriginalClassId(student.classId);
    setIsEditing(true);
  };

  const handleCloseModal = () => {
    setIsEditing(false);
    setEditStudent(null);
    setOriginalClassId(null);
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    if (!editStudent) return;
    try {
      const birthDate = new Date(editStudent.birthDate);
      const age = new Date().getFullYear() - birthDate.getFullYear();
      const newClassId = editStudent.classId;

      const studentData = {
        firstName: editStudent.firstName,
        lastName: editStudent.lastName,
        fullName: `${editStudent.firstName} ${editStudent.lastName}`,
        studentId: editStudent.studentId,
        birthDate: toIsoString(birthDate),
        age: age,
        classId: newClassId,
        updatedAt: toIsoString(new Date())
      };

      if (originalClassId && originalClassId !== newClassId) {
        // Moving student to a different class — delete old doc, create new one.
        // iOS requires a UUID doc key + `id` field; reuse the existing UUID when
        // present so the student's identity is preserved across the move.
        const studentUuid = asUuid(editStudent.id) || crypto.randomUUID();
        await deleteDoc(doc(db, 'classes', originalClassId, 'students', editStudent.id));
        await setDoc(doc(db, 'classes', newClassId, 'students', studentUuid), {
          ...studentData,
          id: studentUuid,
          uuid: studentUuid,
          currentEnrollmentStatus: editStudent.currentEnrollmentStatus || 'Active',
          isActive: true,
          createdAt: toIsoString(new Date()),
          schoolId: adminData?.schoolId || null
        });
        // Keep the top-level sign-up mirror (iOS saveStudentSignupRecord shape) in sync
        if (editStudent.studentId) {
          await setDoc(doc(db, 'students', editStudent.studentId), {
            studentId: editStudent.studentId,
            schoolId: adminData?.schoolId || null,
            classId: newClassId || null,
            firstName: editStudent.firstName,
            lastName: editStudent.lastName,
            updatedAt: toIsoString(new Date())
          }, { merge: true });
        }
        console.log('Student moved to new class with id:', studentUuid);
      } else {
        await updateDoc(doc(db, 'classes', newClassId, 'students', editStudent.id), studentData);
      }

      dataCacheService.invalidatePath(`classes/${newClassId}/students`);
      if (originalClassId && originalClassId !== newClassId) {
        dataCacheService.invalidatePath(`classes/${originalClassId}/students`);
      }
      handleCloseModal();
      loadStudents();
    } catch (error) {
      console.error('Error updating student:', error);
      alert('Failed to update student');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const studentsByClass = getStudentsByClass();

  // Edit modal — shared by the list page and the detail page
  const studentEditModal = isEditing && editStudent && (
    <div className="modal-overlay" onClick={handleCloseModal}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Edit Student</h3>
        <form onSubmit={handleUpdateStudent}>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                value={editStudent.firstName}
                onChange={(e) => setEditStudent({ ...editStudent, firstName: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                value={editStudent.lastName}
                onChange={(e) => setEditStudent({ ...editStudent, lastName: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Student ID *</label>
              <input
                type="text"
                value={editStudent.studentId}
                disabled
                style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
              />
            </div>
            <div className="form-group">
              <label>Class *</label>
              <select
                value={editStudent.classId}
                onChange={(e) => setEditStudent({ ...editStudent, classId: e.target.value })}
                required
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name} ({cls.grade})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Birth Date *</label>
            <input
              type="date"
              value={editStudent.birthDate}
              onChange={(e) => setEditStudent({ ...editStudent, birthDate: e.target.value })}
              required
            />
          </div>
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={handleCloseModal}>
              Cancel
            </button>
            <button type="submit" className="save-button">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Student ID card overlay — shared by the list page and the detail page
  const studentIDCardModal = showIDCard && (
    <StudentIDCard
      student={showIDCard}
      schoolName={adminData?.schoolName || 'KN Education Portal'}
      onClose={() => setShowIDCard(null)}
    />
  );

  // Full-screen student detail page — replaces the list
  if (detailStudent) {
    const student = students.find(s => s.id === detailStudent.id) || detailStudent;
    const studentClass = classes.find(c => c.id === student.classId);
    const className = studentClass?.name || detailClassName || 'N/A';
    const studentParents = getStudentParents(student);
    const studentName = student.fullName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unknown';
    const isActive = student.isActive !== false;
    const enrollmentStatus = student.currentEnrollmentStatus || (isActive ? 'Active' : 'Inactive');
    return (
      <div className="admin-students-view">
        <div className="detail-page">
          <button className="detail-back-button" onClick={() => setDetailStudent(null)}>
            ◄ Back to Students
          </button>

          <div className="detail-hero">
            <div className="detail-avatar">
              {student.photoURL
                ? <img src={student.photoURL} alt={studentName} />
                : (student.firstName ? student.firstName[0].toUpperCase() : 'S')}
            </div>
            <div className="detail-hero-info">
              <h2>{studentName}</h2>
              <p className="detail-subtitle">Student ID: {student.studentId || 'N/A'}</p>
              <div className="detail-badges">
                {className !== 'N/A' && <span className="detail-badge">{className}</span>}
                <span className={`detail-badge ${isActive ? 'green' : 'red'}`}>{enrollmentStatus}</span>
              </div>
            </div>
            <div className="detail-hero-actions">
              <button
                className="detail-btn detail-btn-id"
                onClick={() => setShowIDCard({ ...student, className, grade: studentClass?.grade || '' })}
              >
                🆔 ID Card
              </button>
              <button className="detail-btn detail-btn-edit" onClick={() => handleEditClick(student)}>
                Edit
              </button>
              <button
                className="detail-btn detail-btn-delete"
                onClick={async () => { if (await handleDeleteStudent(student)) setDetailStudent(null); }}
              >
                Delete
              </button>
            </div>
          </div>

          <div className="detail-grid">
            <div className="detail-card">
              <h3>Student Information</h3>
              <div className="detail-field">
                <span className="field-label">Name</span>
                <span className="field-value">{studentName}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Student ID</span>
                <span className="field-value">{student.studentId || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Class</span>
                <span className="field-value">{className}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Grade</span>
                <span className="field-value">{studentClass?.grade || student.grade || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Date of Birth</span>
                <span className="field-value">{student.birthDate ? new Date(student.birthDate).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Age</span>
                <span className="field-value">{student.age != null ? student.age : 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Email</span>
                <span className="field-value">{student.email || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Enrollment Status</span>
                <span className="field-value">{enrollmentStatus}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Created</span>
                <span className="field-value">{student.createdAt ? new Date(student.createdAt).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>

            <div className="detail-card">
              <h3>Parent / Guardian ({studentParents.length})</h3>
              {studentParents.length === 0 ? (
                (student.parentName || student.parentEmail) ? (
                  <div className="detail-list">
                    <div className="detail-list-item">
                      <span className="item-name">{student.parentName || 'Parent'}</span>
                      <span className="item-meta">{student.parentEmail || ''}</span>
                    </div>
                  </div>
                ) : (
                  <p className="detail-empty">No linked parent found</p>
                )
              ) : (
                <div className="detail-list">
                  {studentParents.map(parent => (
                    <div key={parent.id} className="detail-list-item">
                      <span className="item-name">{parent.fullName || `${parent.firstName || ''} ${parent.lastName || ''}`.trim() || parent.username || 'Parent'}</span>
                      <span className="item-meta">
                        {parent.email || ''}{parent.phoneNumber ? ` • ${parent.phoneNumber}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {studentEditModal}
        {studentIDCardModal}
      </div>
    );
  }

  return (
    <div className="admin-students-view">
      <div className="view-header">
        <h2>Students</h2>
        <button className="add-button" onClick={() => setShowAddForm(true)}>
          + Add Student
        </button>
      </div>
      <StatCards items={[['Students',students.length],['Classes',classes.length],['Active Classes',new Set(students.map(s => s.classId)).size],['Filtered',getFilteredStudents().length]]} />

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search students..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      <p className="count-text">Students: {getFilteredStudents().length} in {studentsByClass.length} classes</p>

      {showAddForm && (
        <div className="add-student-form">
          <h3>Create New Student</h3>
          <form onSubmit={handleAddStudent}>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={newStudent.firstName}
                  onChange={(e) => setNewStudent({ ...newStudent, firstName: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={newStudent.lastName}
                  onChange={(e) => setNewStudent({ ...newStudent, lastName: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Student ID</label>
                <input
                  type="text"
                  value="Auto-generated"
                  disabled
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
                <small style={{ color: '#6b7280' }}>Format: [SCHOOL_CODE]-[CODE]-[YEAR]</small>
              </div>
              <div className="form-group">
                <label>Class *</label>
                <select
                  value={newStudent.classId}
                  onChange={(e) => setNewStudent({ ...newStudent, classId: e.target.value })}
                  required
                >
                  <option value="">Select Class</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name} ({cls.grade})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Birth Date *</label>
              <input
                type="date"
                value={newStudent.birthDate}
                onChange={(e) => setNewStudent({ ...newStudent, birthDate: e.target.value })}
                required
              />
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit">Create Student</button>
            </div>
          </form>
        </div>
      )}

      {studentsByClass.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No Students</h3>
          <p>Add students to get started</p>
        </div>
      ) : (
        <div className="students-list">
          {studentsByClass.map(({ class: cls, students: classStudents }) => (
            <div key={cls.id} className="class-section">
              <div className="class-section-header">
                <h4>{cls.name}</h4>
                <span>Grade: {cls.grade} • {classStudents.length} students</span>
              </div>
              {classStudents.map(student => (
                <div key={student.id} className="student-card">
                  <div className="student-avatar" onClick={() => { setDetailStudent(student); setDetailClassName(cls.name); }}>
                    {student.firstName ? student.firstName[0] : 'S'}
                  </div>
                  <div className="student-info" onClick={() => { setDetailStudent(student); setDetailClassName(cls.name); }}>
                    <h5>{student.fullName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unknown'}</h5>
                    <p>ID: {student.studentId}</p>
                    <p>Class: {cls.name}</p>
                    {student.email && <p>Email: {student.email}</p>}
                  </div>
                  <div className="student-status">
                    <span className="status-dot active"></span>
                  </div>
                  <div className="student-actions">
                    <button
                      className="id-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowIDCard({ ...student, className: cls.name, grade: cls.grade });
                      }}
                      title="Print ID Card"
                    >
                      🆔
                    </button>
                    <button
                      className="delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteStudent(student);
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {studentEditModal}

      {studentIDCardModal}
    </div>
  );
}

export default AdminStudentsView;
